// Tiny Web Audio cues for the voice-input flow (no asset files). Soft, short tones:
//  - start:  a gentle two-note rising blip when recording begins
//  - accept: a pleasant rising chime when the user accepts the transcript
//  - reject: a soft low tone when the user discards it
// Lazily creates a single shared AudioContext (resumed on first use — browsers require a user
// gesture, which all three triggers are). Fails silent if Web Audio is unavailable.

let ctx: AudioContext | null = null

function getCtx(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      ctx = new Ctor()
    }
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/** Play one short tone (sine) with a quick attack + gentle exponential release. */
function tone(freq: number, startAt: number, duration: number, peak = 0.08) {
  const c = getCtx()
  if (!c) return
  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  const t = c.currentTime + startAt
  gain.gain.setValueAtTime(0.0001, t)
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, t + duration)
  osc.connect(gain).connect(c.destination)
  osc.start(t)
  osc.stop(t + duration + 0.02)
}

export const uiSounds = {
  /** Recording started — soft rising two-note blip. */
  recordStart() { tone(523.25, 0, 0.12); tone(659.25, 0.09, 0.14) },
  /** Transcript accepted — pleasant rising chime. */
  accept() { tone(659.25, 0, 0.1); tone(987.77, 0.08, 0.16, 0.09) },
  /** Transcript rejected / cancelled — soft low tone. */
  reject() { tone(392.0, 0, 0.16, 0.06) },
}
